import type {
  Communication,
  CallStatus,
  CriteriaScore,
  TranscriptSegment,
  QueueRecommendation,
} from '@entities/communication/model/types';
import type {FeedRow} from './types';
import {mockClientSid} from '../clients/mock-sid';

/**
 * Сырая коммуникация из meerec.srv.bc (см. meerec/core/communications/models.py).
 * Поля участников/менеджера/оценки лежат в metadata/participants. Структура
 * стабилизирована после полного pipeline (n8n→AssemblyAI→OpenRouter):
 *
 * - `metadata.score` — итоговая оценка, шкала задаётся `metadata.scoreScale`
 *   (на бэке используется 1–5; UI ожидает 0–10, поэтому в адаптере умножаем).
 * - `metadata.criteria[]` — 5 критериев с оценкой по той же шкале.
 * - `metadata.status` — 'sale' | 'noresult' | 'wrongNumber' (без 'robot').
 * - `metadata.result` — свободная строка от LLM, не enum.
 * - `metadata.goalAchieved`, `metadata.promotion`, `metadata.audioUrl` —
 *   новые поля, фронт пока их частично использует.
 */
export interface BackendCommunication {
  /** bcf сериализует UUIDPK через alias `_id`; держим оба варианта. */
  sid?: string;
  _id?: string;
  source: string;
  provider: string;
  externalId: string;
  occurredAt: string;
  receivedAt?: string;
  text?: string | null;
  payloadRef?: string | null;
  mediaRef?: string | null;
  participants?: Array<Record<string, unknown>>;
  metadata?: Record<string, unknown>;
  processingStatus?:
    | 'pending'
    | 'ingested'
    | 'preprocessing'
    | 'ready'
    | 'failed';
  status?: string | number;
  /** Плоский результат анализа по плану (в корне ответа, не в metadata). */
  formResult?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string | null;
}

const asString = (v: unknown, fallback = ''): string =>
  typeof v === 'string' ? v : fallback;

const asNumber = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

const asBool = (v: unknown, fallback = false): boolean =>
  typeof v === 'boolean' ? v : fallback;

const findParticipant = (
  participants: Array<Record<string, unknown>> | undefined,
  role: string,
): Record<string, unknown> | undefined =>
  participants?.find((p) => asString(p['role']).toLowerCase() === role);

const CALL_STATUS_VALUES: CallStatus[] = [
  'sale',
  'noresult',
  'robot',
  'wrongNumber',
];

const asCallStatus = (v: unknown): CallStatus =>
  CALL_STATUS_VALUES.includes(v as CallStatus) ? (v as CallStatus) : 'noresult';

/** Коэффициент пересчёта оценки бэка в фронтовую шкалу 0–10. */
const scaleFactor = (scoreScale: unknown): number => {
  const s = asNumber(scoreScale, 10);
  if (s <= 0) return 1;
  return 10 / s;
};

/** Маппинг русских значений температуры в enum фронта. */
const asTemperature = (v: unknown): 'cold' | 'warm' | 'hot' | null => {
  if (typeof v !== 'string') return null;
  const lower = v.trim().toLowerCase();
  if (lower === 'холодный' || lower === 'cold') return 'cold';
  if (lower === 'тёплый' || lower === 'теплый' || lower === 'warm')
    return 'warm';
  if (lower === 'горячий' || lower === 'hot') return 'hot';
  return null;
};

/** Маппинг channel в тип коммуникации фронта.
 *
 * `channel` определяет категорию: все встречи — `'meeting'` (подтип —
 * online/offline/... живёт в `source` в FeedRow). Телефония — `'phone'`. */
const asChannelType = (v: unknown): 'phone' | 'meeting' => {
  if (typeof v !== 'string') return 'phone';
  const lower = v.trim().toLowerCase();
  if (
    lower === 'meeting' ||
    lower === 'online-meeting' ||
    lower === 'offline-meeting' ||
    lower === 'online' ||
    lower === 'in-person' ||
    lower === 'in_person' ||
    lower === 'video'
  )
    return 'meeting';
  return 'phone';
};

export const adaptBackendCommunication = (
  raw: BackendCommunication,
): Communication => {
  const metadata = (raw.metadata ?? {}) as Record<string, unknown>;
  const manager = findParticipant(raw.participants, 'manager') ?? {};
  const client = findParticipant(raw.participants, 'client') ?? {};

  const managerName = asString(
    manager['displayName'] ?? metadata['managerName'],
  );
  const managerIdRaw = asString(
    manager['sid'] ?? manager['_id'] ?? metadata['managerId'],
  );
  // Если бэк не вернул managerId — используем displayName, чтобы фильтр/маршрут
  // в карточку менеджера на UI не ломался.
  const managerId = managerIdRaw || managerName;

  const factor = scaleFactor(metadata['scoreScale']);

  const rawCriteria = Array.isArray(metadata['criteria'])
    ? (metadata['criteria'] as Array<Record<string, unknown>>)
    : [];
  const criteria: CriteriaScore[] = rawCriteria.map((c) => ({
    name: asString(c['name']),
    score: Math.round(asNumber(c['score']) * factor),
  }));

  const goalAchieved = asBool(metadata['goalAchieved']);
  const resultText = asString(metadata['resultText']) || null;
  const resultDisplay = asString(metadata['result']) || null;

  return {
    id: asString(raw.sid ?? raw._id),
    dateTime: raw.occurredAt,
    managerId,
    manager: managerName,
    client: asString(client['displayName'] ?? metadata['clientName']),
    phone: asString(client['phone'] ?? metadata['phone']),
    duration: asNumber(metadata['durationSeconds']),
    score: asNumber(metadata['score']) * factor,
    isTargeted: asBool(metadata['isTargeted'], true),
    isPrimary: typeof metadata['isPrimary'] === 'boolean' ? metadata['isPrimary'] : null,
    // Канал/направление — нейтральны для ядра и приходят в metadata.
    direction: metadata['direction'] === 'inbound' ? 'in' : 'out',
    channelType: asChannelType(metadata['channelType'] ?? metadata['channel']),
    answered: asBool(metadata['answered'], true),
    status: asCallStatus(metadata['status']),
    nextStep:
      typeof metadata['nextStep'] === 'string'
        ? (metadata['nextStep'] as string)
        : null,
    queueItem: (metadata['queueItem'] as QueueRecommendation | null) ?? null,
    transcript: Array.isArray(metadata['transcript'])
      ? (metadata['transcript'] as TranscriptSegment[])
      : [],
    criteria,
    objections: Array.isArray(raw.formResult?.['objections'])
      ? (raw.formResult?.['objections'] as string[])
      : [],
    objectionsHandled: Array.isArray(raw.formResult?.['objectionsHandled'])
      ? (raw.formResult?.['objectionsHandled'] as string[])
      : [],
    analysisRunId:
      typeof metadata['analysisRunId'] === 'string'
        ? (metadata['analysisRunId'] as string)
        : null,
    // result — витрина-строка от LLM (русский, company-specific); KPI по ней не считаются.
    result: resultDisplay,
    resultText,
    goalAchieved,
    saleMade: asBool(metadata['saleMade']),
    agreementReached: asBool(metadata['agreementReached']),
    withoutConflict: asBool(metadata['withoutConflict']),
    conflictSource:
      typeof metadata['conflictSource'] === 'string'
        ? (metadata['conflictSource'] as string)
        : null,
    // Ред-флаг: сквозной сигнал из плана; reason вне enum — как есть, без валидации.
    redFlag: asBool(metadata['redFlag']),
    redFlagReason:
      typeof metadata['redFlagReason'] === 'string'
        ? (metadata['redFlagReason'] as string)
        : null,
    refusalReason:
      typeof metadata['refusalReason'] === 'string'
        ? (metadata['refusalReason'] as string)
        : null,
    mainObjection:
      typeof metadata['mainObjection'] === 'string'
        ? (metadata['mainObjection'] as string)
        : null,
    scriptCompliance: asBool(metadata['scriptCompliance']),
    promoApplied:
      asBool(metadata['promoApplied']) ||
      (typeof metadata['promotion'] === 'string' &&
        metadata['promotion'] !== 'нет'),
    summary: asString(metadata['summary'] ?? raw.text),
    recommendation:
      typeof metadata['recommendation'] === 'string'
        ? (metadata['recommendation'] as string)
        : null,
    nonTargetReason:
      typeof metadata['nonTargetReason'] === 'string'
        ? (metadata['nonTargetReason'] as string)
        : null,
    temperature: asTemperature(metadata['temperature']),
    scriptFollowed:
      typeof metadata['scriptFollowed'] === 'boolean'
        ? (metadata['scriptFollowed'] as boolean)
        : typeof metadata['scriptCompliance'] === 'boolean'
          ? (metadata['scriptCompliance'] as boolean)
          : null,
    formResult: raw.formResult ?? null,
  };
};

// ─── Адаптер строки ленты colver (FeedRow → Communication) ──────────────────

/** Русские подписи критериев базового плана (`result.criteria.*`). */
export const CRITERIA_LABELS: Record<string, string> = {
  greeting: 'Приветствие',
  needDiscovery: 'Выявление потребности',
  // Демо: не «Презентация» — это имя занято бинарным критерием karting-рубрики
  // (display-rubric.ts), и балльный критерий базового плана показывался бы «Да/Нет».
  presentation: 'Презентация продукта',
  objectionHandling: 'Работа с возражениями',
  closing: 'Закрытие сделки',
  nextStepFixation: 'Следующий шаг',
};

/** Обратный словарь: русское имя критерия → ключ схемы. */
const CRITERIA_KEYS_BY_LABEL: Record<string, string> = Object.fromEntries(
  Object.entries(CRITERIA_LABELS).map(([k, v]) => [v, k]),
);

/** Мнемоника бэка → фронтовый CallStatus. */
const CALL_STATUS_FROM_API: Record<string, CallStatus> = {
  sale: 'sale',
  'no-result': 'noresult',
  robot: 'robot',
  'wrong-number': 'wrongNumber',
};

/** Фронтовый CallStatus → мнемоника бэка (для MQL-фильтров). */
export const CALL_STATUS_TO_API: Record<CallStatus, string> = {
  sale: 'sale',
  noresult: 'no-result',
  robot: 'robot',
  wrongNumber: 'wrong-number',
};

const asOptString = (v: unknown): string | null =>
  typeof v === 'string' && v ? v : null;

/** `result.criteria` (объект ключ→оценка /10) → CriteriaScore[] с русскими именами. */
const criteriaFromResult = (v: unknown): CriteriaScore[] => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return [];
  return Object.entries(v as Record<string, unknown>)
    .filter(([, score]) => typeof score === 'number')
    .map(([key, score]) => ({
      name: CRITERIA_LABELS[key] ?? key,
      score: score as number,
    }));
};

/**
 * Адаптирует строку ленты colver к фронтовой сущности `Communication`.
 * Шкала оценок сохраняется /10.
 * Имя менеджера здесь не резолвится — его подставляют хуки по members.
 */
export const adaptFeedRow = (row: FeedRow): Communication => {
  const result = (row.result ?? {}) as Record<string, unknown>;
  const metadata = (row.metadata ?? {}) as Record<string, unknown>;
  // Номер клиента: адаптер MTS (mts-vats) кладёт его в metadata.clientNumber;
  // `phone` — легаси-ключ моков. Берём clientNumber первым.
  const phone = asString(metadata['clientNumber'] ?? metadata['phone']);
  const callStatusRaw = asString(result['callStatus']);

  return {
    id: row.communicationId,
    dateTime: row.occurredAt,
    managerId: row.userSid ?? '',
    manager: asString(metadata['managerName']),
    client: asOptString(result['clientName']) ?? phone,
    phone,
    duration: asNumber(row.durationSeconds),
    score: asNumber(result['score']),
    isTargeted: asBool(result['isTargeted'], false),
    isPrimary: typeof result['isPrimary'] === 'boolean' ? result['isPrimary'] : null,
    direction: row.direction === 'inbound' ? 'in' : 'out',
    channelType: asChannelType(row.channel),
    answered: true,
    status: CALL_STATUS_FROM_API[callStatusRaw] ?? 'noresult',
    nextStep: asOptString(result['nextStep']),
    queueItem: null,
    transcript: [],
    criteria: criteriaFromResult(result['criteria']),
    objections: Array.isArray(result['objections'])
      ? (result['objections'] as string[])
      : [],
    objectionsHandled: Array.isArray(result['objectionsHandled'])
      ? (result['objectionsHandled'] as string[])
      : [],
    analysisRunId: null,
    result: asOptString(result['result']),
    resultText: asOptString(result['resultText']),
    temperature: asTemperature(result['temperature']),
    scriptFollowed:
      typeof result['scriptCompliance'] === 'boolean'
        ? (result['scriptCompliance'] as boolean)
        : null,
    goalAchieved:
      typeof result['goalAchieved'] === 'boolean'
        ? (result['goalAchieved'] as boolean)
        : null,
    saleMade:
      typeof result['saleMade'] === 'boolean'
        ? (result['saleMade'] as boolean)
        : null,
    agreementReached:
      typeof result['agreementReached'] === 'boolean'
        ? (result['agreementReached'] as boolean)
        : null,
    withoutConflict:
      typeof result['withoutConflict'] === 'boolean'
        ? (result['withoutConflict'] as boolean)
        : null,
    conflictSource: asOptString(result['conflictSource']),
    redFlag:
      typeof result['redFlag'] === 'boolean' ? (result['redFlag'] as boolean) : null,
    redFlagReason: asOptString(result['redFlagReason']),
    refusalReason: asOptString(result['refusalReason']),
    mainObjection: asOptString(result['mainObjection']),
    scriptCompliance: asBool(result['scriptCompliance']),
    promoApplied: asBool(result['promoApplied']),
    summary: asString(result['summary']),
    recommendation: asOptString(result['recommendation']),
    nonTargetReason: asOptString(result['nonTargetReason']),
    processingStatus: row.processingStatus,
    resultStatus: row.resultStatus ?? null,
    hasTranscript: Boolean(row.hasTranscript),
    hasMedia: Boolean(row.hasMedia),
    lastError: row.lastError ?? null,
  };
};

/**
 * Обратный маппинг для mock-режима: фронтовая `Communication` (mock-генератор)
 * → строка ленты в формате контракта. Используется, чтобы страница «Звонки»
 * работала по единому пути (FeedRow) и в mock-, и в real-режиме.
 */
export const feedRowFromCommunication = (c: Communication): FeedRow => ({
  communicationId: c.id,
  occurredAt: c.dateTime,
  receivedAt: c.dateTime,
  provider: 'mock',
  source: 'telephony',
  processingStatus: c.processingStatus ?? 'ready',
  userSid: c.managerId,
  direction: c.direction === 'in' ? 'inbound' : 'outbound',
  channel: c.channelType === 'meeting' ? 'meeting' : 'phone-call',
  durationSeconds: c.duration,
  hasMedia: false,
  hasText: true,
  hasTranscript: c.hasTranscript ?? c.transcript.length > 0,
  lastError: c.lastError ?? null,
  resultStatus: c.resultStatus ?? 'succeeded',
  result: {
    isTargeted: c.isTargeted,
    callStatus: CALL_STATUS_TO_API[c.status],
    result: c.result,
    resultText: c.resultText ?? null,
    score: c.score,
    criteria: Object.fromEntries(
      c.criteria.map((cr) => [
        CRITERIA_KEYS_BY_LABEL[cr.name] ?? cr.name,
        cr.score,
      ]),
    ),
    objections: c.objections,
    objectionsHandled: c.objectionsHandled ?? [],
    mainObjection: c.mainObjection,
    scriptCompliance: c.scriptCompliance,
    promoApplied: c.promoApplied,
    nextStep: c.nextStep,
    agreementReached: c.agreementReached ?? null,
    saleMade: c.saleMade ?? null,
    withoutConflict: c.withoutConflict ?? null,
    conflictSource: c.conflictSource ?? null,
    redFlag: c.redFlag ?? null,
    redFlagReason: c.redFlagReason ?? null,
    refusalReason: c.refusalReason ?? null,
    summary: c.summary,
    recommendation: c.recommendation,
    temperature: c.temperature ?? null,
    goalAchieved: c.goalAchieved ?? null,
    nonTargetReason: c.nonTargetReason,
    clientName: c.client || null,
  },
  planVersion: 1,
  metadata: {
    userSid: c.managerId,
    phone: c.phone,
    managerName: c.manager,
    // Ярлык клиента — лента карточки клиента фильтрует по нему (`metadata.clientSid`).
    clientSid: c.client ? mockClientSid(c.client) : null,
  },
});
